import {
  faChartBar,
  faMap,
  faPlug,
  faGear,
  faQuestionCircle,
  faShield,
  faHome,
  faSearch,
  faDatabase,
  faPuzzlePiece,
} from '@fortawesome/pro-regular-svg-icons';
import { faChartLine } from '@fortawesome/pro-duotone-svg-icons';
import { NavigationConfig } from './types';
import { getNewAdminUrl, getMarketUrl } from '../../../utils/urls';

interface NavigationConfigOptions {
  tenantId: string;
  flags?: {
    demo?: boolean;
    onboarding_intercom?: boolean;
    onboarding_hubspot?: boolean;
    onboarding_googlesheet?: boolean;
    profiles_demo?: boolean;
  };
  user?: {
    superKudu?: boolean;
    role?: string;
    permissions?: Array<{
      tenant?: number | null;
    }>;
  };
  /** Optional click handler for Sales Copilot item */
  onSalesCopilotClick?: () => void;
}

export function getNavigationConfig({
  tenantId,
  flags = {},
  user,
  onSalesCopilotClick,
}: NavigationConfigOptions): NavigationConfig {
  const isAdmin =
    user?.superKudu || user?.role === 'admin' || user?.role === 'architect';
  const isSuperKudu = user?.superKudu === true;
  const primaryTenantId =
    user?.permissions?.[0]?.tenant !== undefined &&
    user?.permissions?.[0]?.tenant !== null
      ? String(user.permissions[0].tenant)
      : null;
  const shouldAddMarketAnalyzerImpersonationParams = Boolean(
    isSuperKudu &&
      tenantId &&
      (!primaryTenantId || tenantId !== primaryTenantId)
  );

  const buildMarketAnalyzerUrl = (
    baseUrl: string,
    currentTenantId: string,
    shouldAddImpersonationParams: boolean
  ): string => {
    if (!baseUrl || !shouldAddImpersonationParams) return baseUrl;

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('from', 'admin');
      url.searchParams.set('impersonationTenantId', currentTenantId);
      return url.toString();
    } catch {
      return baseUrl;
    }
  };

  const sections: NavigationConfig['sections'] = [
    {
      // Home section (no label) - appears above PLANNING
      items: [
        ...(flags.demo === true
          ? [
              {
                label: 'Home',
                href: `${getNewAdminUrl()}/org/${tenantId}`,
                icon: faHome,
                isExternal: true, // External - points to new admin
                activeMatcher: (path: string) =>
                  path.includes('/homepage') || /^\/org\/\d+$/.test(path),
                description: 'Return to your dashboard overview',
              },
            ]
          : flags.onboarding_intercom === true ||
            flags.onboarding_hubspot === true ||
            flags.onboarding_googlesheet === true
          ? [
              {
                label: 'Get Started',
                href: `/org/${tenantId}`,
                icon: faHome,
                activeMatcher: (path: string) => /^\/org\/\d+$/.test(path),
                description: 'Get started with your setup',
              },
            ]
          : [
              {
                label: 'Home',
                href: `${getNewAdminUrl()}/org/${tenantId}`,
                icon: faHome,
                isExternal: true, // External - points to new admin
                activeMatcher: (path: string) =>
                  path.includes('/homepage') || /^\/org\/\d+$/.test(path),
                description: 'Return to your dashboard overview',
              },
            ]),
      ],
    },
    {
      label: 'PLANNING',
      items: [
        {
          label: 'Market Analyzer',
          href: buildMarketAnalyzerUrl(
            getMarketUrl(),
            tenantId,
            shouldAddMarketAnalyzerImpersonationParams
          ),
          icon: faChartLine,
          isExternal: true, // External - points to market analyzer service
          activeMatcher: (path: string) => path.includes('/market-analyser'),
          description: 'Analyze market trends and opportunities',
        },
      ],
    },
    {
      label: 'SEGMENTATION',
      items: [
        {
          label: 'Score Lookup',
          href: `/org/${tenantId}/profiles`,
          icon: faSearch,
          isExternal: false, // Internal in old admin
          description: 'Search and analyze company profiles',
          badgeText: 'New',
        },
        {
          label: 'Predictions',
          href: `/org/${tenantId}/predictions`,
          icon: faChartBar, // Match new admin - uses faChartBar
          isExternal: false, // Internal in old admin
          description: 'View predictive insights and forecasts',
        },
        {
          label: 'Data Studio',
          href: `/org/${tenantId}/data-studio`,
          icon: faDatabase,
          isExternal: false, // Internal in old admin
          description: 'Explore and visualize your data',
        },
      ],
    },
    {
      label: 'PROSPECTING',
      items: [
        {
          label: 'Open Sales Copilot',
          href: '#', // Href is not used when onClick is provided
          icon: faPuzzlePiece,
          isExternal: true, // External in old admin
          description: 'Access the Sales Copilot application',
          onClick: onSalesCopilotClick,
          variant: 'highlight', // Makes it blue
        },
      ],
    },
  ];

  // Note: standaloneLinks removed - moved to bottomLinks to match new admin structure

  const bottomLinks = [
    {
      label: 'Mapping',
      href: `/org/${tenantId}/mapping`,
      icon: faMap,
      isExternal: false, // Internal in old admin
      description: 'Configure data mapping and transformations',
    },
    {
      label: 'Data',
      href: `${getNewAdminUrl()}/org/${tenantId}/data`,
      icon: faDatabase,
      isExternal: true, // External - points to new admin
      description: 'Explore and discover your integration data',
    },
    ...(isAdmin
      ? [
          {
            label: 'Integrations',
            href: `/org/${tenantId}/integrations`,
            icon: faPlug,
            description: 'Manage your third-party integrations',
            visibleFor: ['admin', 'superKudu'] as Array<'admin' | 'superKudu'>,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            label: 'Settings',
            href: `/org/${tenantId}/settings`,
            icon: faGear,
            activeMatcher: (path: string) =>
              path.includes('/settings') && !path.includes('/user-settings'),
            description: 'Configure account and organization settings',
            visibleFor: ['admin', 'superKudu'] as Array<'admin' | 'superKudu'>,
          },
        ]
      : []),
    {
      label: 'Help & Support',
      href: `/org/${tenantId}/help`,
      icon: faQuestionCircle,
      isExternal: false, // Internal in old admin
      description: 'Get help and contact support',
    },
    ...(isSuperKudu
      ? [
          {
            label: 'SuperKudu Admin',
            href: `${getNewAdminUrl()}/superkudu/onboardings`,
            icon: faShield,
            isExternal: true, // External - points to new admin
            description: 'SuperKudu administration panel',
            visibleFor: ['superKudu'] as Array<'admin' | 'superKudu'>,
          },
        ]
      : []),
  ];

  return {
    sections,
    bottomLinks,
  };
}
